import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Container,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import { Link } from 'react-router-dom';
import PollIcon from '@mui/icons-material/Poll';
import BarChartIcon from '@mui/icons-material/BarChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useSelector } from 'react-redux';
import userChecker from '../utils/userChecker';
import theme from '../components/Theme';

export default function HomeScreen() {
  const adminUserLogin = useSelector((state) => state.adminUserLogin);
  const { adminUserInfo } = adminUserLogin;
  const { user } = adminUserInfo;

  const cards = [
    {
      title: 'Session Review',
      description:
        "View today's sessions where customers can submit ratings and complete detailed reviews.",
      icon: PollIcon,
      color: '#C38A2D',
      bgColor: '#FDF4E3',
      link: '/session-review',
      buttonText: 'Start Review',
      public: true,
    },
    {
      title: 'Results',
      description:
        'Access comprehensive session review results and analytics dashboard.',
      icon: BarChartIcon,
      color: '#A0721F',
      bgColor: '#F5EFE7',
      link: '/session-review/result',
      buttonText: 'View Results',
      public: false,
    },
    {
      title: 'Detailed Results',
      description:
        'Dive deep into detailed analytics with advanced filtering and insights.',
      icon: AnalyticsIcon,
      color: '#8B5A1A',
      bgColor: '#F9F6F0',
      link: '/session-review/detailed-result',
      buttonText: 'Detailed Analytics',
      public: false,
    },
    {
      title: 'Review Timeline',
      description: 'Track trends and patterns in customer feedback over time.',
      icon: TimelineIcon,
      color: '#6B4C17',
      bgColor: '#FAF8F4',
      link: '/session-review/review-timeline',
      buttonText: 'View Timeline',
      public: false,
    },
  ];

  const isUserAuthorized = user && userChecker(user.kind);

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #C38A2D 0%, #A0721F 100%)',
            color: 'white',
            borderRadius: 3,
            p: 6,
            mb: 6,
            mt: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '50%',
              zIndex: 0,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <AssignmentIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
            </Box>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: theme.palette.secondary.main,
              }}
            >
              CD Survey
            </Typography>
            <Typography
              variant="p"
              sx={{
                opacity: 0.9,
                color: '#FFFFFF',
                fontWeight: 400,
                mb: 2,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                display: 'block',
              }}
            >
              Comprehensive session review and analytics platform for enhanced
              customer experience management
            </Typography>{' '}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  opacity: 0.9,
                  px: 3,
                  py: 1,
                  borderRadius: 20,
                  fontWeight: 500,
                }}
              >
                Time-ranged Analytics
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  opacity: 0.9,
                  px: 3,
                  py: 1,
                  borderRadius: 20,
                  fontWeight: 500,
                }}
              >
                Review Insights
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  opacity: 0.9,
                  px: 3,
                  py: 1,
                  borderRadius: 20,
                  fontWeight: 500,
                }}
              >
                Review Data
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Cards Section */}
        <Grid container spacing={4}>
          {cards.map((card, index) => {
            const IconComponent = card.icon;
            const shouldShow = card.public || isUserAuthorized;

            if (!shouldShow) return null;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-8px)',
                      borderColor: card.color,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: card.bgColor,
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: 40,
                            color: card.color,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: '#1e293b',

                          fontSize: '1.5rem',
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#64748b',
                          lineHeight: 1.6,
                          minHeight: 60,
                        }}
                      >
                        {card.description}
                      </Typography>
                    </Box>

                    <Button
                      component={Link}
                      to={card.link}
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}CC 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${card.color}DD 0%, ${card.color}AA 100%)`,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 20px ${card.color}40`,
                          color: '#FFFFFF',
                        },
                        transition: 'all 0.3s ease-in-out',
                        color: '#FFFFFF',
                      }}
                    >
                      {card.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer Info */}
        <Box sx={{ textAlign: 'center', mt: 8, py: 4 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.95rem',
            }}
          >
            Powered by Club Dynasty advanced analytics and customer feedback
            insights
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
